import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Trophy, ArrowRight, Mail, Zap, Users, Shield } from "lucide-react";
import { Picture } from "@/components/Picture";
import leaderboardImage from "@/assets/leaderboard_image.png";
import leaderboardImageWebP from "@/assets/leaderboard_image.webp";

export default function Auth() {
  const [email, setEmail] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(true);
  const [loading, setLoading] = useState(false);
  const { signInWithMagicLink } = useAuth();
  const navigate = useNavigate();

  // Prefill email from URL params (e.g. from /go landing page).
  // Also stash `next` so the post-auth handler can redirect there after
  // the magic-link round-trip (the link itself can't carry query state
  // since Supabase owns the redirect URL).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const prefill = params.get("email");
    if (prefill) setEmail(prefill);
    const next = params.get("next");
    if (next && next.startsWith("/") && !next.startsWith("//")) {
      localStorage.setItem("post_auth_next", next);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms of Service and Privacy Policy to continue.");
      return;
    }
    setLoading(true);
    const { error } = await signInWithMagicLink(email);
    if (error) {
      toast.error(error.message);
    } else {
      // Fire `Lead` (not `CompleteRegistration`) here — at this point the
      // user has handed over their email but hasn't actually finished
      // registration yet (that happens after they click the magic link).
      if (typeof window.fbq === "function") {
        window.fbq("track", "Lead", { content_name: "magic_link_request" });
      }
      navigate(`/check-email?email=${encodeURIComponent(email)}`);
    }
    setLoading(false);
  };

  useEffect(() => {
    document.title = "Sign In | FCBracket";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", "Sign in or create an account to build your free bracket and compete in tournaments.");
  }, []);

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Subtle leaderboard photo as page backdrop, heavily dimmed so the
          form and value props stay the focal point. */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <Picture
          src={leaderboardImage}
          webp={leaderboardImageWebP}
          alt=""
          loading="eager"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.08]"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-background via-background/95 to-background/80" />
      </div>

      <div className="relative">
        <Header />
        <div className="container mx-auto px-4 py-6 md:py-14">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-16 items-center max-w-5xl mx-auto">
            {/* Value props: below form on mobile, left on desktop. */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="order-last lg:order-first space-y-6"
            >
              <div>
                <p className="font-display text-xs text-primary tracking-[0.2em] mb-3">
                  THE OFFICIAL BRACKET CHALLENGE
                </p>
                <h2 className="font-display text-3xl md:text-5xl text-foreground leading-[1.05]">
                  Your picks.<br />
                  Your friends.<br />
                  <span className="text-primary">Real prizes.</span>
                </h2>
              </div>

              {/* Prize pool card */}
              <div className="surface-card p-5 border-primary/30 bg-primary/5 flex items-center gap-4">
                <Trophy className="w-8 h-8 text-primary shrink-0" strokeWidth={2.25} />
                <div>
                  <p className="font-display text-3xl text-primary leading-none">$5,000</p>
                  <p className="font-body text-xs text-muted-foreground mt-1">
                    Prize pool for the top scorer. No entry fee.
                  </p>
                </div>
              </div>

              {/* Value bullets */}
              <ul className="space-y-3">
                <li className="flex items-start gap-3 font-body text-sm text-foreground">
                  <Zap className="w-4 h-4 text-primary shrink-0 mt-0.5" strokeWidth={2.25} />
                  <span>Free to play. Lock your bracket in under 2 minutes.</span>
                </li>
                <li className="flex items-start gap-3 font-body text-sm text-foreground">
                  <Users className="w-4 h-4 text-primary shrink-0 mt-0.5" strokeWidth={2.25} />
                  <span>Private groups for your office, friends, or fan club.</span>
                </li>
                <li className="flex items-start gap-3 font-body text-sm text-foreground">
                  <Shield className="w-4 h-4 text-primary shrink-0 mt-0.5" strokeWidth={2.25} />
                  <span>Real-time scoring. One-tap sign in. No password ever.</span>
                </li>
              </ul>

              {/* Social proof */}
              <p className="font-body text-xs text-muted-foreground">
                Join 41+ official fan-club groups — Austin FC, Arsenal, USMNT, and more.
              </p>
            </motion.div>

            {/* Sign-in form */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08 }}
              className="w-full max-w-md mx-auto lg:mx-0 lg:justify-self-end"
            >
              <form onSubmit={handleSubmit} className="surface-card p-6 md:p-7 space-y-5">
                <div className="space-y-1">
                  <h1 className="font-display text-2xl md:text-3xl text-foreground">
                    Sign in
                  </h1>
                  <p className="text-muted-foreground font-body text-sm">
                    We'll send a magic link. No password needed.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                    inputMode="email"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    className="bg-secondary/50 border-border text-foreground h-12"
                  />
                </div>

                <div className="flex items-start gap-2">
                  <Checkbox
                    id="agree-terms"
                    checked={agreedToTerms}
                    onCheckedChange={(v) => setAgreedToTerms(v === true)}
                    className="mt-0.5"
                  />
                  <Label
                    htmlFor="agree-terms"
                    className="font-body text-[10px] text-muted-foreground leading-relaxed cursor-pointer"
                  >
                    I agree to the{" "}
                    <Link to="/terms" target="_blank" rel="noopener" className="text-primary hover:underline">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link to="/privacy" target="_blank" rel="noopener" className="text-primary hover:underline">
                      Privacy Policy
                    </Link>
                    . I understand prizes are not guaranteed and depend on each tournament hitting its minimum participation threshold.
                  </Label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !agreedToTerms}
                  className="w-full h-12 font-semibold text-base gap-2"
                >
                  <Mail className="w-5 h-5" />
                  {loading ? "Sending..." : "Send Magic Link"}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}